import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  DashboardMetricsField,
  IpTeamListDto,
  OrganizationListDto,
  ProjectListDto,
  VendorListDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { CACHE_TIME } from '../constants';

export const DASHBOARD_USER_PREFERENCES_KEY = 'dashboard-user-preferences';

export const DASHBOARD_PINNED_ORGANIZATION_IDS_KEY =
  'internal-portal-pinned-organization-ids';
export const DASHBOARD_PINNED_VENDOR_IDS_KEY =
  'internal-portal-pinned-vendor-ids';
export const DASHBOARD_PINNED_PROJECT_IDS_KEY =
  'internal-portal-pinned-project-ids';
export const DASHBOARD_PINNED_TEAM_IDS_KEY = 'internal-portal-pinned-team-ids';

export const INTERNAL_PORTAL_WORK_IN_PROGRESS_DISMISSED_KEY =
  'internal-portal-work-in-progress-dismissed';

type PinnedOrganizationsQuery = Parameters<
  typeof api.internalPortal.dashboardControllerGetPinnedOrganizations
>[0];

type PinnedVendorsQuery = Parameters<
  typeof api.internalPortal.dashboardControllerGetPinnedVendors
>[0];

type PinnedProjectsQuery = Parameters<
  typeof api.internalPortal.dashboardControllerGetPinnedProjects
>[0];

type PinnedTeamsQuery = Parameters<
  typeof api.internalPortal.dashboardControllerGetPinnedTeams
>[0];

export const useGetPinnedDashboardOrganizations = (
  query: Omit<PinnedOrganizationsQuery, 'key'> & { key?: string },
  options?: UseQueryOptions<OrganizationListDto>
): UseQueryResult<OrganizationListDto> =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalDashboardPinnedOrganizations,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.dashboardControllerGetPinnedOrganizations({
        ...query,
        key: query.key ?? DASHBOARD_PINNED_ORGANIZATION_IDS_KEY,
      }),
  });

export const useGetPinnedDashboardVendors = (
  query: Omit<PinnedVendorsQuery, 'key'> & { key?: string },
  options?: UseQueryOptions<VendorListDto>
): UseQueryResult<VendorListDto> =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalDashboardPinnedVendors,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.dashboardControllerGetPinnedVendors({
        ...query,
        key: query.key ?? DASHBOARD_PINNED_VENDOR_IDS_KEY,
      }),
  });

export const useGetPinnedDashboardProjects = (
  query: Omit<PinnedProjectsQuery, 'key'> & { key?: string },
  options?: UseQueryOptions<ProjectListDto>
): UseQueryResult<ProjectListDto> =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalDashboardPinnedProjects,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.dashboardControllerGetPinnedProjects({
        ...query,
        key: query.key ?? DASHBOARD_PINNED_PROJECT_IDS_KEY,
      }),
  });

export const useGetPinnedDashboardTeams = (
  query: Omit<PinnedTeamsQuery, 'key'> & { key?: string },
  options?: UseQueryOptions<IpTeamListDto>
): UseQueryResult<IpTeamListDto> =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalDashboardPinnedTeams,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.dashboardControllerGetPinnedTeams({
        ...query,
        key: query.key ?? DASHBOARD_PINNED_TEAM_IDS_KEY,
      }),
  });

export const DASHBOARD_METRIC_FIELDS = [
  DashboardMetricsField.LastEditedTask,
  DashboardMetricsField.LastEditedTicket,
  DashboardMetricsField.Notifications,
  DashboardMetricsField.CrmCount,
  DashboardMetricsField.Tickets,
  DashboardMetricsField.Tasks,
];

export const useInternalPortalDashboardMetricsQuery = (
  fields: DashboardMetricsField[] = DASHBOARD_METRIC_FIELDS
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalDashboardMetrics,
      QueryKey.Operation.One,
      fields,
    ],
    queryFn: async () =>
      api.internalPortal.dashboardControllerGetMetrics({
        fields,
      }),
  });
